<template>
<b-sidebar
    id="food-form-sidebar"
    :visible="isFoodFormSidebarActive"
    bg-variant="white"
    sidebar-class="sidebar-lg"
    shadow
    backdrop
    no-header
    right
    @shown="getData(foodData)"
    @hidden="resetForm"
    @change="(val) => $emit('update:is-food-form-sidebar-active', val)"
>
    <template #default="{ hide }">
        <div
            class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1"
        >
            <h5 class="mb-0">Food Form</h5>
            <feather-icon
                class="ml-1 cursor-pointer"
                icon="XIcon"
                size="16"
                @click="close(hide)"
            />
        </div>
        <validation-observer #default="{ handleSubmit }" ref="refFormObserver">
            <b-form
                class="p-2"
                @submit.prevent="handleSubmit(onSubmit)"
                @reset.prevent="resetForm"
            >
                <b-tabs v-model="tabIndex">
                    <b-tab title="Content">
                        <b-tabs content-class="mt-1" v-if="langs.length > 0">
                            <b-tab
                                v-for="(lang, key) in langs"
                                :key="'tab-' + key"
                                :title="lang.name"
                            >
                                <validation-provider
                                    #default="validationContext"
                                    :name="'name_' + lang.code"
                                    rules="required"
                                >
                                    <b-form-group
                                        label="Menu Name"
                                        :label-for="'name_' + lang.code"
                                    >
                                        <b-form-input
                                            :id="'name_' + lang.code"
                                            v-model="Data.title[lang.code]"
                                            autofocus
                                            :state="getValidationState(validationContext)"
                                            trim
                                            :placeholder="'name (' + lang.code + ')'"
                                            @input="changeTitle(lang.code)"
                                        />
                                        <b-form-invalid-feedback>
                                            {{ validationContext.errors[0] }}
                                        </b-form-invalid-feedback>
                                    </b-form-group>
                                </validation-provider>
                                <validation-provider
                                    #default="validationContext"
                                    :name="'description_' + lang.code"
                                    rules="min:0"
                                >
                                    <b-form-group
                                        label="Description"
                                        :label-for="'description_' + lang.code"
                                    >
                                        <quill-editor
                                            :options="editorOptions"
                                            :id="'description_' + lang.code"
                                            v-model="Data.description[lang.code]"
                                        />
                                        <b-form-invalid-feedback>
                                            {{ validationContext.errors[0] }}
                                        </b-form-invalid-feedback>
                                    </b-form-group>
                                </validation-provider>
                                <validation-provider
                                    #default="validationContext"
                                    :name="'slug' + lang.code"
                                    rules="required"
                                >
                                    <b-form-group 
                                        label="Slug" 
                                        :label-for="'slug' + lang.code"
                                    >
                                        <b-form-input
                                            :id="'slug' + lang.code"
                                            v-model="Data.slug[lang.code]"
                                            autofocus
                                            :state="getValidationState(validationContext)"
                                            trim
                                            :placeholder="'Slug (' + lang.code + ')'"
                                        />
                                        <b-form-invalid-feedback>
                                            {{ validationContext.errors[0] }}
                                        </b-form-invalid-feedback>
                                    </b-form-group>
                                </validation-provider>
                            </b-tab>
                        </b-tabs>
                        <hr />
                        <validation-provider
                            #default="validationContext"
                            :name="'price'"
                            rules="required"
                        >
                            <b-form-group
                                label="Price"
                                :label-for="'price'"
                            >
                                <b-form-input
                                    :id="'price'"
                                    v-model="Data.price"
                                    autofocus
                                    :state="getValidationState(validationContext)"
                                    trim
                                    :placeholder="'price'"
                                />
                                <b-form-invalid-feedback>
                                    {{ validationContext.errors[0] }}
                                </b-form-invalid-feedback>
                            </b-form-group>
                        </validation-provider>
                        <validation-provider
                            #default="validationContext"
                            name="food_sub_category_id"
                        >
                            <b-form-group label="Sub Category" label-for="food_sub_category_id">
                                <v-select
                                    id="food_sub_category_id"
                                    v-model="Data.sub_category"
                                    :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                                    label="title"
                                    :reduce="(option) => option.id"
                                    :options="subCatagoriesData.map((g) => ({
                                        title: g.title[currentLocale],
                                        id: g.id
                                    }))"
                                />
                                <b-form-invalid-feedback>
                                    {{ validationContext.errors[0] }}
                                </b-form-invalid-feedback>
                            </b-form-group>
                        </validation-provider>
                        <validation-provider
                            #default="validationContext"
                            name="allergy_id"
                            rules="min:0"
                        >
                            <b-form-group label="Allergies" label-for="allergy_id">
                                <v-select
                                    id="allergy_id"
                                    v-model="Data.allergies"
                                    multiple
                                    :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                                    label="title"
                                    :options="
                                        allergiesData.map((g) => ({
                                            title: g.title[currentLocale],
                                            id: g.id,
                                        }))
                                    "
                                />
                                <b-form-invalid-feedback>
                                    {{ validationContext.errors[0] }}
                                </b-form-invalid-feedback>
                            </b-form-group>
                        </validation-provider>
                        <validation-provider
                            #default="validationContext"
                            name="spicy_level"
                            rules="min:0"
                        >
                            <b-form-group label="Spicy Rating (0-5)" label-for="spicy_level">
                                <b-form-input
                                    :id="'spicy_level'"
                                    v-model="Data.spicy_level"
                                    autofocus
                                    :state="getValidationState(validationContext)"
                                    trim
                                    :placeholder="'spicy level'"
                                />
                                <b-form-invalid-feedback>
                                    {{ validationContext.errors[0] }}
                                </b-form-invalid-feedback>
                            </b-form-group>
                        </validation-provider>

                        <validation-provider
                            #default="validationContext"
                            name="IsActive"
                            rules="required"
                        >
                            <b-form-group label="Is Active" label-for="IsActive">
                                <b-form-checkbox
                                    id="isactive"
                                    checked="true"
                                    class="custom-control-success"
                                    v-model="Data.is_active"
                                    switch
                                />
                                <b-form-invalid-feedback>
                                {{ validationContext.errors[0] }}
                                </b-form-invalid-feedback>
                            </b-form-group>
                        </validation-provider>
                    </b-tab>
                    <b-tab title="SEO">
                        <validation-provider
                            #default="validationContext"
                            name="meta_title"
                            rules="min:0"
                        >
                            <b-form-group label="Meta Title" label-for="meta_title">
                            <b-form-input
                                id="meta_title"
                                v-model="Data.meta_title"
                                autofocus
                                :state="getValidationState(validationContext)"
                                trim
                                placeholder="Meta Title"
                            />
                            <b-form-invalid-feedback>
                                {{ validationContext.errors[0] }}
                            </b-form-invalid-feedback>
                            </b-form-group>
                        </validation-provider>
                        <validation-provider
                            #default="validationContext"
                            name="meta_description"
                            rules="min:0"
                            
                        >
                            <b-form-group
                            label="Meta Description"
                            label-for="meta_description"
                            >
                            <b-form-input
                                id="meta_description"
                                v-model="Data.meta_description"
                                autofocus
                                :state="getValidationState(validationContext)"
                                trim
                                placeholder="Meta Description"
                            />
            
                            <b-form-invalid-feedback>
                                {{ validationContext.errors[0] }}
                            </b-form-invalid-feedback>
                            </b-form-group>
                        </validation-provider>
                        <validation-provider
                            #default="validationContext"
                            name="meta_keywords"
                            rules="min:0"
                        >
                            <b-form-group
                            label="Meta Keywords"
                            label-for="meta_keywords"
                            >
                            <b-form-input
                                id="meta_keywords"
                                v-model="Data.meta_keyword"
                                autofocus
                                :state="getValidationState(validationContext)"
                                trim
                                placeholder="Meta Keywords"
                            />
                            <b-form-invalid-feedback>
                                {{ validationContext.errors[0] }}
                            </b-form-invalid-feedback>
                            </b-form-group>
                        </validation-provider>
                    </b-tab>
                </b-tabs>
                <div class="d-flex mt-2">
                    <b-button
                        v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                        variant="outline-success"
                        class="mr-2"
                        type="submit"
                    >
                        Save
                    </b-button>
                    <b-button
                        v-ripple.400="'rgba(186, 191, 199, 0.15)'"
                        type="button"
                        variant="outline-danger"
                        @click="close(hide)"
                    >
                        Cancel
                    </b-button>
                </div>
            </b-form>
        </validation-observer>
    </template>
</b-sidebar>
</template>

<script>
    import { $themeConfig } from '@themeConfig'
    import {
        BSidebar, BForm, BFormGroup, BFormInput, BFormTextarea, BFormCheckbox, BFormInvalidFeedback,
        BButton, BTabs, BTab, BCard, BCardText, BFormRadio, BContainer, BRow, BCol,
    } from "bootstrap-vue";
    import { required, alphaNum, email, password,confirmed } from '@validations'
    import { ValidationProvider, ValidationObserver } from "vee-validate";
    import vSelect from "vue-select";
    import store from "@/store";
    import languagesStoreModule from "@/views/languages/languagesStoreModule";
    import formValidation from "@core/comp-functions/forms/form-validation";
    import Ripple from "vue-ripple-directive";
    import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
    import { useToast } from "vue-toastification/composition";
    import { slugify } from "@/@fake-db/utils";
    import { ref, onUnmounted } from "@vue/composition-api";
    // import { useInputImageRenderer } from "@core/comp-functions/forms/form-utils";
    import { quillEditor } from "vue-quill-editor";
    import "quill/dist/quill.snow.css";
    import "quill/dist/quill.core.css";
    import "quill/dist/quill.bubble.css";
    import Treeselect from '@riophae/vue-treeselect'
    import '@riophae/vue-treeselect/dist/vue-treeselect.css'
    import folderStoreModule from '../../files/folderStoreModule'
    import FileLists from '../../uploadStorage/FileLists.vue'

    export default {
        components: {
            BSidebar, BForm, BFormGroup, BFormInput, BFormTextarea, BFormCheckbox, BFormInvalidFeedback,
            BButton, BTabs, BTab, BCard, BCardText, vSelect, ValidationProvider, ValidationObserver, 
            quillEditor, BFormRadio, Treeselect, BContainer, BRow, BCol, FileLists,
        },
        directives: {
            Ripple,
        },
        model: {
            prop: "isFoodFormSidebarActive",
            event: "update:is-food-form-sidebar-active",
        },
        props: {
            isFoodFormSidebarActive: {
                type: Boolean,
                required: true,
            },
            foodData: {
                type: Object,
                required: false,
            },
        },
        computed: {
            currentLocale() {
                return this.$i18n.locale;
            },
            folderOptions(){
                return this.folders.map((folder) => this.formatFolder(folder))
            },
            editorOptions (){
                return $themeConfig.snowOption
            }
        },
        methods:{
            formatFolder(folder){
                return{
                    id: folder.id,
                    label: folder.title,
                    children: folder.subFolders.length? folder.subFolders.map(this.formatFolder): undefined
                }
            },
        },
        setup(props, { emit }){
            const {  appRef } = $themeConfig.app
            const toast = useToast();
            const subCatagoriesData = ref([]);
            const folders = ref([])
            const tabIndex = ref(0);
            const langs = ref([]);
            const parent = ref(0);
            const selected = ref("")
            const model = ref(null)
            const images = ref([])
            const selectImages = ref([])
            const selectCoverImage = ref(null)
            const allergiesData = ref([])
            const isModalFileListsShow = ref(false)
            const uploadType = ref('')

            const LANGUAGES_STORE_MODULE_NAME = "app-languages";
            if (!store.hasModule(LANGUAGES_STORE_MODULE_NAME)) store.registerModule(LANGUAGES_STORE_MODULE_NAME, languagesStoreModule);
            onUnmounted(() => {
                if (store.hasModule(LANGUAGES_STORE_MODULE_NAME))store.unregisterModule(LANGUAGES_STORE_MODULE_NAME);
            });

            const FOLDER_STORE_MODULE_NAME = 'files-folder'
            if (!store.hasModule(FOLDER_STORE_MODULE_NAME)) store.registerModule(FOLDER_STORE_MODULE_NAME, folderStoreModule)
            onUnmounted(() => {
                if (store.hasModule(FOLDER_STORE_MODULE_NAME)) store.unregisterModule(FOLDER_STORE_MODULE_NAME)
            });

            const userStorage = JSON.parse(localStorage.getItem('userData'));
            const getLocalSite = JSON.parse(localStorage.getItem('site'))

            store.dispatch('app-food/getSubCategoryData', {
                sites_id: getLocalSite.id
            })
            .then(response => {
                subCatagoriesData.value = response.data.data
            })

            store.dispatch('app-food/getFoodAllergies', {
                sites_id: getLocalSite.id
            })
            .then(response => {
                allergiesData.value = response.data.data
            })

            const blankData = {
                meta_title: '',
                meta_description: '',
                meta_keyword: '',
                slug: {},
                price: null,
                is_active: true,
                sites_id: getLocalSite.id,
                ordering_count: 0,
                created_by: userStorage.id,
                title: {},
                description: {},
                allergies: [],
                spicy_level: '',
                sub_category: null,
            };

            const Data = ref(blankData);
            const oldValue  =  ref(null)

            const resetData = () => {
                Data.value = blankData;
            };

            const uploadImage = (type) => {
                uploadType.value = type
                isModalFileListsShow.value = true
            }

            const close = (hidden) => {
                for(const lang in oldValue.value?.title ?? {}) {
                    Data.value.title[lang] = oldValue.value.title[lang]
                }

                for(const lang in oldValue.value?.slug ?? {}) {
                    Data.value.slug[lang] = oldValue.value.slug[lang]
                }

                hidden()
            }

            const refesh = () => {
                store.dispatch('files-folder/getFolders', {
                    parent: parent.value,
                    order_by:'ordering_count ASC'
                })
                    .then(response => {
                        const res = response.data.data
                        folders.value = res
                })

                model.value = null
                images.value = []
                selectCoverImage.value = null
                selectImages.value = []
            }

            const getData = async function (data) {
                store.dispatch('app-food/getFolders', {
                    parent: parent.value,
                    order_by:'ordering_count ASC'
                })
                    .then(response => {
                        const res = response.data.data
                        folders.value = res.filter((folder => {
                            return folder.parent === 0
                        }))
                })
                
                Data.value.title = {}
                Data.value.description = {}
                Data.value.slug = {}
                Data.value.price = ''
                Data.value.sub_category = null
                Data.value.allergies = []
                Data.value.meta_title = ''
                Data.value.meta_description = ''
                Data.value.meta_keyword = ''
                Data.value.is_active = true
                Data.value.spicy_level = ''

                if(data) {
                    data.is_active = data.is_active? true: false
                    Data.value = data

                    Data.value = {
                        ...data,
                        allergies : Array.isArray(data.allergies)?data.allergies.map((g) => {
                            return {
                                title: g.title[this.currentLocale],
                                id: g.id, 
                            }
                        })
                        : [],
                        sub_category: data?.sub_category?.id
                    }

                    oldValue.value = JSON.parse(JSON.stringify({...data}))
                }
            }

            const changeTitle  = (lang) => {
                if(lang){
                    Data.value.slug[lang] = slugify(Data.value.title[lang]);
                }
            }

            const eventSelected = (id) => {
                store.dispatch('files-folder/getFolderById', id)
                    .then(response => {
                        const res = response.data.data
                        images.value = res[0].images

                        const existingFolderId = Data?.value?.files[0]?.folder_id

                        if(model.value != existingFolderId){
                            selectCoverImage.value = null
                            selectImages.value = []
                        }
                })
            }

            const eventSelectedCoverImg = (data) => {
                const coverImg = selectCoverImage.value
                if(coverImg==null){
                    selectCoverImage.value = data
                    return
                }

                if(coverImg?.id == data.id){
                    selectCoverImage.value = null
                    return
                }
            }

            const eventImgSelected = (data) => {
                const index = selectImages.value.findIndex(item => item.id === data.id);
                index === -1 ? selectImages.value.push(data) : selectImages.value.splice(index, 1);
            }

            const validateSpicyLevel = (data) => {
                const pattern = /^(?:[0-4](?:\.\d+)?|5(?:\.0*)?)?$/;
                if (!pattern.test(data)) {
                    return false
                } 

                return true
            }

            const onSubmit = function () {
                Data.value.is_active = Data.value.is_active?1:0;

                const checkSpicyLevel = validateSpicyLevel(Data.value.spicy_level)

                if(!checkSpicyLevel && Data.value.spicy_level != null){
                    toast({
                        component: ToastificationContent,
                        position: 'top-right',
                        props: {
                            title: "Error",
                            icon: 'AlertTriangleIcon',
                            variant: 'danger',
                            text: `Please input valid spicy level (ex.0-5)`,
                        },
                    })

                    return
                }

                if(!Data.value.id){
                    store.dispatch('app-food/addFood', Data.value)
                        .then((res) => {
                            toast({
                                component: ToastificationContent,
                                position: 'top-right',
                                props: {
                                    title: `Success`,
                                    icon: 'BellIcon',
                                    variant: 'success',
                                    text: `${res.data.message}`,
                                },
                            });

                            emit('refetch-data')
                            emit('update:is-food-form-sidebar-active', false)
                        })
                        .catch((error) => {
                            toast({
                                component: ToastificationContent,
                                position: 'top-right',
                                props: {
                                    title: "Error",
                                    icon: 'AlertTriangleIcon',
                                    variant: 'danger',
                                    text: `${error?.response?.data?.message??'Something went wrong'}`,
                                },
                            })
                        })
                } else {
                    store.dispatch('app-food/updateFood', Data.value)
                        .then((res) => {
                            toast({
                                component: ToastificationContent,
                                position: 'top-right',
                                props: {
                                    title: `Success`,
                                    icon: 'BellIcon',
                                    variant: 'success',
                                    text: `${res.data.message}`,
                                },
                            });

                            emit('refetch-data')
                            emit('update:is-food-form-sidebar-active', false)
                        })
                        .catch((error) => {
                            toast({
                                component: ToastificationContent,
                                position: 'top-right',
                                props: {
                                    title: "Error",
                                    icon: 'AlertTriangleIcon',
                                    variant: 'danger',
                                    text: `${error?.response?.data?.message??'Something went wrong'}`,
                                },
                            })
                        })
                    
                }
            }

            const getLangs = (filter) => {
                store.dispatch("app-languages/fetchData", filter).then((res) => {
                    if (res.data.success) {
                    langs.value = res.data.data;
                    }
                });
            };

            let filter = {
                is_active:1,
                
            }

            const {
                resetForm,
                getValidationState,
                refFormObserver
            } = formValidation(resetData);

            getLangs(filter);

            const refeshList = async () => {
                emit("refetch-data");
            };

            return {
                langs,
                resetForm,
                getData,
                tabIndex,
                subCatagoriesData,
                Data,
                getValidationState,
                refFormObserver,
                onSubmit,
                refeshList,
                changeTitle,
                resetData,
                folders,
                selected,
                images,
                eventSelected,
                selectImages,
                selectCoverImage,
                eventSelectedCoverImg,
                eventImgSelected,
                model,
                allergiesData,
                isModalFileListsShow,
                uploadImage,
                close,
                oldValue,
                refesh,
                uploadType,
            }
        },
    }
</script>

<style lang="scss">
    @import "@core/scss/vue/libs/quill.scss";

    .selected-img{
        border: solid 5px #FFB341;
    }

    .disable-img{
        cursor: not-allowed;
        opacity: 0.5;
    }
</style>